import { FC, ReactNode, memo, useState } from 'react';
import styled from 'styled-components';
import { Outlet } from 'react-router';

import { useScreenSizes } from '@hooks/useScreenSizes';
import { AgoraEngineProvider } from '@providers/AgoraEngineProvider';
import { AlertProvider } from '@providers/AlertProvider';
import { HomeTabsProvider } from '@providers/HomeTabsProvider';
import { ModalProvider } from '@providers/ModalProvider';
import { RecordedShowProvider } from '@providers/RecordedShowProvider';
import { SearchProvider } from '@providers/SearchProvider';
import {
  HEADER_HEIGHT,
  MOBILE_HEADER_HEIGHT,
  SIDE_MENU_WIDTH,
  BOTTOM_MENU_HEIGHT,
} from '@utils/constants';
import BottomMenu from '@components/bottomMenu';
import Header from '@components/header';
import SideMenu from '@components/sidemenu';

const Container = styled.div<{ $isLocked?: boolean }>`
  width: 100vw;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${({ $isLocked }) =>
    $isLocked &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  `}
`;

const ContentWithSidebar = styled.div`
  width: calc(100vw - ${SIDE_MENU_WIDTH}px);
  height: calc(100dvh - ${HEADER_HEIGHT}px);
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
`;

const ContentFullWidth = styled.div`
  width: 100%;
  height: calc(100dvh - ${HEADER_HEIGHT}px);
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.black};
`;

const TabletContentWithBottomMenu = styled.div`
  width: 100%;
  height: calc(
    100dvh - ${MOBILE_HEADER_HEIGHT}px -
      ${BOTTOM_MENU_HEIGHT}px - env(safe-area-inset-bottom)
  );
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.black};
`;

const FullScreenContent = styled.div`
  width: 100%;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  align-self: stretch;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.black};
`;

const OverlayContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-top: ${MOBILE_HEADER_HEIGHT}px;
    box-sizing: border-box;
  }
`;

const SideMenuOverlay = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    top: ${MOBILE_HEADER_HEIGHT}px;
    height: calc(
      100dvh - ${MOBILE_HEADER_HEIGHT}px -
        ${BOTTOM_MENU_HEIGHT}px - env(safe-area-inset-bottom)
    );
  }
`;

const TabletSideMenu = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: ${MOBILE_HEADER_HEIGHT}px;
  left: ${({ $isOpen }) => ($isOpen ? '0' : `-${SIDE_MENU_WIDTH}px`)};
  width: ${SIDE_MENU_WIDTH}px;
  height: calc(
    100dvh - ${MOBILE_HEADER_HEIGHT}px -
      ${BOTTOM_MENU_HEIGHT}px - env(safe-area-inset-bottom)
  );
  z-index: 1000;
  transition: left 0.3s ease;
`;

const HeaderWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 998;
  }
`;

const BottomMenuWrapper = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 998;
    padding-bottom: env(safe-area-inset-bottom);
  }
`;

type Props = {
  children?: ReactNode;
  showSideMenu?: boolean;
  hideHeaderAndBottomMenu?: boolean;
};

const AppLayout: FC<Props> = ({
  children,
  showSideMenu = true,
  hideHeaderAndBottomMenu = false,
}) => {
  const { isMobile, isTablet } = useScreenSizes();
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] =
    useState<boolean>(showSideMenu);
  const [isTabletSideMenuOpen, setIsTabletSideMenuOpen] =
    useState<boolean>(false);

  const handleSideMenuToggle = () => {
    if (isTablet || isMobile) {
      setIsTabletSideMenuOpen((prev) => !prev);
    } else {
      setIsSideMenuCollapsed(false);
    }
  };

  const closeSideMenu = () => {
    setIsTabletSideMenuOpen(false);
  };

  return (
    <AlertProvider>
      <ModalProvider>
        <SearchProvider>
          <HomeTabsProvider>
            <AgoraEngineProvider>
              <RecordedShowProvider>
                <Container
                  $isLocked={(isTablet || isMobile) && isTabletSideMenuOpen}
                >
                  {!((isTablet || isMobile) && hideHeaderAndBottomMenu) && (
                    <HeaderWrapper>
                      <Header onSideMenuCollapse={handleSideMenuToggle} />
                    </HeaderWrapper>
                  )}

                  {isTablet || isMobile ? (
                    hideHeaderAndBottomMenu ? (
                      <FullScreenContent>
                        {children || <Outlet />}
                      </FullScreenContent>
                    ) : (
                      <>
                        <OverlayContainer>
                          <TabletContentWithBottomMenu>
                            {children || <Outlet />}
                          </TabletContentWithBottomMenu>

                          {showSideMenu && (
                            <>
                              <SideMenuOverlay
                                $isVisible={isTabletSideMenuOpen}
                                onClick={closeSideMenu}
                              />
                              <TabletSideMenu $isOpen={isTabletSideMenuOpen}>
                                <SideMenu
                                  isCollapsed={false}
                                  onClose={closeSideMenu}
                                />
                              </TabletSideMenu>
                            </>
                          )}
                        </OverlayContainer>

                        <BottomMenuWrapper>
                          <BottomMenu onSideMenuClose={closeSideMenu} />
                        </BottomMenuWrapper>
                      </>
                    )
                  ) : showSideMenu ? (
                    <SideMenu
                      isCollapsed={isSideMenuCollapsed}
                      onClose={closeSideMenu}
                    >
                      <ContentWithSidebar>
                        {children || <Outlet />}
                      </ContentWithSidebar>
                    </SideMenu>
                  ) : (
                    <ContentFullWidth>
                      {children || <Outlet />}
                    </ContentFullWidth>
                  )}
                </Container>
              </RecordedShowProvider>
            </AgoraEngineProvider>
          </HomeTabsProvider>
        </SearchProvider>
      </ModalProvider>
    </AlertProvider>
  );
};

export default memo(AppLayout);
